'use client';

import './checkbox.scss';

import { trackClick } from '@sola/tracking/lib/analytics/events.ts';

import { cn } from '../../utils/helpers.ts';
import {
  Field,
  FieldErrorIcon,
  FieldErrorMessage,
  type FieldProps,
  FieldWrapper,
  getErrorElId,
} from '../field/field.tsx';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../tooltip/tooltip.tsx';

export interface CheckboxProps extends Partial<
  Omit<
    FieldProps,
    | 'children'
    | 'min'
    | 'max'
    | 'valueAsNumber'
    | 'valueAsDate'
    | 'value'
    | 'defaultValue'
  >
> {
  label: string;
  labelContent?: React.ReactNode;
  checked?: boolean;
  checkboxRef?: React.MutableRefObject<HTMLInputElement>;
  defaultChecked?: boolean;
  disabledTooltip?: React.ReactNode;
  isCentered?: boolean;
  /**
   * Visual variant of the checkbox.
   * - `default` (default): inline checkbox with label
   * - `tinted`: variant that tints the container based on state
   *   (checked → subtle container, disabled+checked → neutral mild, error → negative subtle)
   */
  variant?: 'default' | 'tinted';
}

/**
 * Checkbox component which leverages our field functionality.
 */
export const Checkbox: React.FC<CheckboxProps> = ({
  label,
  checkboxRef,
  defaultChecked,
  isCentered = true,
  labelContent,
  variant = 'default',
  disabledTooltip,
  ...props
}) => {
  const hasDisabledTooltip = Boolean(props.disabled && disabledTooltip);

  return (
    <Field {...(props as FieldProps)} controlRef={checkboxRef}>
      {({ controlProps, error }) => {
        const errorElId = getErrorElId(controlProps.id);
        const labelNode = (
          <label
            className="Checkbox__label ml-comfy-xs"
            htmlFor={controlProps.id}
          >
            {labelContent || label}
          </label>
        );
        return (
          <>
            <FieldWrapper
              isCentered={false}
              className={cn('Checkbox', {
                'align-center': isCentered,
                'Checkbox--tinted': variant === 'tinted',
              })}
            >
              <FieldErrorIcon error={error} />
              <input
                className="Checkbox__input shrink-0"
                type="checkbox"
                defaultChecked={defaultChecked}
                onClick={() => {
                  trackClick(
                    label || 'Unlabeled Checkbox',
                    undefined,
                    'Checkbox__input',
                  );
                }}
                onChange={props.onChange}
                disabled={props.disabled}
                aria-errormessage={errorElId}
                {...controlProps}
                aria-label={hasDisabledTooltip ? label : undefined}
                ref={controlProps.ref || checkboxRef}
              />
              {hasDisabledTooltip ? (
                <Tooltip>
                  <TooltipTrigger>
                    <button className="Checkbox__label-trigger" type="button">
                      <span className="Checkbox__label ml-comfy-xs">
                        {labelContent || label}
                      </span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent align="center" size="sm" usePortal>
                    {disabledTooltip}
                  </TooltipContent>
                </Tooltip>
              ) : (
                labelNode
              )}
            </FieldWrapper>
            <FieldErrorMessage error={error} id={errorElId} />
          </>
        );
      }}
    </Field>
  );
};
